<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AuthModal from "@/components/AuthModal.vue";
import ConnectivityBanner from "@/components/ConnectivityBanner.vue";
import { useAuthStore } from "@/stores/auth";

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const search = ref("");
const downloadVisible = ref(true);
const authOpen = ref(false);
const authMode = ref<"login" | "register">("login");
const isHome = computed(() => route.name === "home");
const isDownload = computed(() => route.name === "download");

onMounted(() => auth.initialize());
watch(
  () => route.query.auth,
  (value) => {
    if (value === "login" || value === "register") {
      authMode.value = value;
      authOpen.value = true;
    }
  },
  { immediate: true },
);
watch(
  () => route.path,
  async (path, previousPath) => {
    if (!previousPath || path === previousPath) return;
    await nextTick();
    document.querySelector<HTMLElement>("#main-content")?.focus({
      preventScroll: true,
    });
  },
);

function submitSearch() {
  const query = search.value.trim();
  if (query.length >= 2) {
    router.push({ name: "search", query: { q: query } });
  }
}

function openAuth(mode: "login" | "register" = "login") {
  authMode.value = mode;
  authOpen.value = true;
}

async function closeAuth() {
  authOpen.value = false;
  if (!route.query.auth) return;
  const query = { ...route.query };
  delete query.auth;
  delete query.redirect;
  await router.replace({ path: route.path, query });
}

async function authenticated() {
  const redirect =
    typeof route.query.redirect === "string" ? route.query.redirect : "";
  authOpen.value = false;
  if (redirect && redirect.startsWith("/")) {
    await router.replace(redirect);
    return;
  }
  await closeAuth();
}

function goDownload() {
  router.push({ name: "download" });
}
</script>

<template>
  <div class="app-shell reference-shell">
    <a class="skip-link" href="#main-content">跳到主要内容</a>
    <ConnectivityBanner />
    <header class="ref-desktop-header">
      <div class="ref-desktop-nav">
        <RouterLink class="ref-logo" to="/" aria-label="178直播首页">
          <img src="/demo-assets/navlogo.png" alt="178直播" />
        </RouterLink>
        <nav class="ref-primary-nav" aria-label="主导航">
          <RouterLink to="/">首页</RouterLink>
          <RouterLink to="/score/football"
            >比分 <span class="nav-caret">▼</span></RouterLink
          >
          <RouterLink to="/lives">全部直播</RouterLink>
          <RouterLink to="/schedules">赛程</RouterLink>
          <RouterLink to="/data">数据</RouterLink>
          <RouterLink to="/video">视频</RouterLink>
          <RouterLink to="/news">资讯</RouterLink>
        </nav>
        <div class="ref-header-actions">
          <button type="button" title="下载客户端" @click="goDownload">
            <span class="action-icon round">↓</span>下载
          </button>
          <RouterLink v-if="auth.isAuthenticated" to="/profile">
            <span class="action-icon user">●</span>
            {{ auth.profile?.nickname || "我的" }}
          </RouterLink>
          <button
            v-else
            type="button"
            title="登录或注册"
            @click="openAuth('login')"
          >
            <span class="action-icon user">●</span>登录/注册
          </button>
        </div>
      </div>
    </header>

    <header class="ref-mobile-header">
      <RouterLink class="ref-mobile-logo" to="/">
        <img src="/demo-assets/mobile-logo.png" alt="178" />
      </RouterLink>
      <form
        class="ref-mobile-search"
        role="search"
        @submit.prevent="submitSearch"
      >
        <span aria-hidden="true"></span>
        <input
          v-model="search"
          aria-label="搜索球队"
          autocomplete="off"
          placeholder="搜索球队"
        />
      </form>
      <RouterLink class="ref-tv-link" to="/lives" aria-label="直播频道"
        >TV</RouterLink
      >
      <RouterLink
        class="ref-account-link"
        :to="auth.isAuthenticated ? '/profile' : '/login'"
        aria-label="登录或个人中心"
      >
        <span></span>
      </RouterLink>
    </header>

    <main
      id="main-content"
      :key="route.path"
      tabindex="-1"
      :class="[
        'page-frame',
        { 'home-page-frame': isHome, 'download-page-frame': isDownload },
      ]"
    >
      <RouterView />
    </main>

    <button
      v-if="isHome && downloadVisible"
      class="ref-pc-download"
      type="button"
      @click="goDownload"
    >
      <img
        class="download-copy"
        src="/demo-assets/download-copy.png"
        alt="热门体育直播，精彩尽在掌握"
      />
      <img
        class="download-button"
        src="/demo-assets/download-button.png"
        alt="立即下载"
      />
    </button>

    <div v-if="isHome && downloadVisible" class="ref-mobile-download">
      <img src="/demo-assets/mobile-logo.png" alt="" />
      <strong>热门赛事，一键直达</strong>
      <button type="button" @click="goDownload">下载APP</button>
      <button
        class="close-download"
        type="button"
        aria-label="关闭下载提示"
        @click="downloadVisible = false"
      >
        ×
      </button>
    </div>

    <nav class="ref-mobile-nav" aria-label="移动端导航">
      <RouterLink to="/">
        <span class="nav-home"></span>
        <b>首页</b>
      </RouterLink>
      <RouterLink to="/messages">
        <span class="nav-chat"></span>
        <b>聊天</b>
      </RouterLink>
      <RouterLink class="event-link" to="/lives">
        <i>HOT</i>
        <span class="nav-event"></span>
        <b>赛事</b>
      </RouterLink>
      <RouterLink to="/score/football">
        <span class="nav-score"></span>
        <b>比分</b>
      </RouterLink>
      <RouterLink to="/data">
        <span class="nav-data"></span>
        <b>数据</b>
      </RouterLink>
    </nav>

    <AuthModal
      :open="authOpen"
      :initial-mode="authMode"
      @close="closeAuth"
      @authenticated="authenticated"
    />
  </div>
</template>
